// Header drag-to-reorder for the VISIBLE columns of a table (Customize mode).
//
// Local + pointer aware: the drop indicator follows which HALF of a header
// you're over, so a column can land AFTER the last one (a left-only indicator
// couldn't express that). Indices are positions in the visible column slice;
// the reorder writes back through the FULL order array so hidden columns hold
// their place.
import { useState, type DragEvent } from 'react'

export interface ColumnDragOptions<K extends string> {
	columnOrder: K[]
	visibleColumns: Set<K>
	onColumnOrderChange: (next: K[]) => void
}

export const useColumnDrag = <K extends string>({
	columnOrder,
	visibleColumns,
	onColumnOrderChange
}: ColumnDragOptions<K>) => {
	const [dragCol, setDragCol] = useState<number | null>(null)
	const [dropCol, setDropCol] = useState<{ index: number; side: 'left' | 'right' } | null>(null)
	const startColDrag = (i: number, e: DragEvent) => {
		setDragCol(i)
		e.dataTransfer.effectAllowed = 'move'
		e.dataTransfer.setData('text/plain', String(i))
		// Drag the whole header cell as the ghost, not just the dots grip. A real
		// <th> can't show a border-radius (border-collapse clips the corners
		// square), so instead of snapshotting the cell we render its content into a
		// free-floating rounded box and use THAT as the drag image, removing it on
		// the next tick once the browser has rasterised it.
		const th = (e.currentTarget as HTMLElement).closest('th')
		if (th) {
			const r = th.getBoundingClientRect()
			const cs = getComputedStyle(th)
			const ghost = document.createElement('div')
			ghost.style.cssText = [
				'position:fixed',
				'top:0',
				'left:-9999px',
				'display:flex',
				'align-items:center',
				'box-sizing:border-box',
				`width:${r.width}px`,
				`height:${r.height}px`,
				`padding:${cs.paddingTop} ${cs.paddingRight} ${cs.paddingBottom} ${cs.paddingLeft}`,
				`font:${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize}/${cs.lineHeight} ${cs.fontFamily}`,
				`color:${cs.color}`,
				'background:rgb(var(--cg-canvas))',
				'border:1px solid rgb(var(--cg-border))',
				'border-radius:8px',
				'box-shadow:0 6px 16px rgba(0,0,0,0.14)',
				'overflow:hidden'
			].join(';')
			// The DS TableHead wraps children in its own span, so the label/grip row
			// is the first descendant div (not a direct child). Clone that.
			const inner = th.querySelector('div')
			if (inner) {
				const innerClone = inner.cloneNode(true) as HTMLElement
				// The grip dots carry the `cg-grip-in` entrance animation (opacity/scale
				// from 0). A fresh clone restarts it from frame 0, and the drag image is
				// rasterised on this same tick — so the dots would be captured invisible.
				// Drop the animation class on the clone so they render at rest.
				innerClone
					.querySelectorAll('.cg-grip-in')
					.forEach((el) => el.classList.remove('cg-grip-in'))
				ghost.appendChild(innerClone)
			}
			document.body.appendChild(ghost)
			e.dataTransfer.setDragImage(ghost, e.clientX - r.left, e.clientY - r.top)
			window.setTimeout(() => ghost.remove(), 0)
		}
	}
	const overCol = (i: number, e: DragEvent) => {
		if (dragCol === null) return
		e.preventDefault()
		e.dataTransfer.dropEffect = 'move'
		const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
		const side: 'left' | 'right' = e.clientX < r.left + r.width / 2 ? 'left' : 'right'
		setDropCol((prev) => (prev && prev.index === i && prev.side === side ? prev : { index: i, side }))
	}
	const endColDrag = () => {
		setDragCol(null)
		setDropCol(null)
	}
	const dropOnCol = (e: DragEvent) => {
		if (dragCol === null || !dropCol) return endColDrag()
		e.preventDefault()
		const from = dragCol
		const target = dropCol
		const visible = columnOrder.filter((k) => visibleColumns.has(k))
		// Insertion slot in the original visible array; right-half drop = after.
		let slot = target.side === 'right' ? target.index + 1 : target.index
		if (from < slot) slot -= 1 // account for removing the dragged column first
		if (!(from < 0 || from >= visible.length || slot < 0 || slot > visible.length - 1 || slot === from)) {
			const [moved] = visible.splice(from, 1)
			visible.splice(slot, 0, moved)
			let i = 0
			onColumnOrderChange(columnOrder.map((k) => (visibleColumns.has(k) ? visible[i++] : k)))
		}
		endColDrag()
	}
	return { dragCol, dropCol, startColDrag, overCol, endColDrag, dropOnCol }
}

// Reconcile a persisted column order with the current key set: keep known keys
// in their saved order, append any new keys, drop keys that no longer exist.
export const reconcileColumnOrder = <K extends string>(saved: K[], all: K[]): K[] => {
	const kept = saved.filter((k) => all.includes(k))
	const added = all.filter((k) => !kept.includes(k))
	return [...kept, ...added]
}
